import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Con qué rapidez puedo empezar SAINT® TMS?",
    answer:
      "Los pacientes de pago directo suelen comenzar en días a semanas, según la programación. Los pasos antes del tratamiento son consulta, fMRI y programación de la semana de 5 días. La verificación del seguro puede añadir tiempo. Contáctenos para hablar de su calendario específico.",
  },
  {
    question: "¿Cuánto tarda la aprobación de SAINT®?",
    answer:
      "Si usa seguro, la verificación de beneficios y la autorización previa pueden tardar de días a semanas según su plan. Los pacientes de pago directo suelen comenzar antes porque no se necesita autorización. Recomendamos iniciar el proceso de verificación temprano.",
  },
  {
    question: "¿Puedo empezar SAINT® la misma semana que mi consulta?",
    answer:
      "Empezar la misma semana es poco común porque la fMRI debe completarse y procesarse antes del tratamiento. Sin embargo, los pacientes de pago directo con flexibilidad de programación a veces pueden comenzar en días a semanas. Contáctenos para hablar de programación acelerada.",
  },
];

export const esHowQuicklyStart = makeEsArticle({
  slug: "how-quickly-can-i-start-saint",
  title: "¿Con qué rapidez puedo empezar SAINT® TMS? | Neuro Wellness",
  description:
    "¿Con qué rapidez puede empezar SAINT® TMS? Conozca el calendario desde la consulta hasta el tratamiento, incluida la programación de la resonancia y el proceso de evaluación.",
  h1: "¿Con qué rapidez puedo empezar SAINT® TMS?",
  breadcrumbLabel: "Empezar SAINT®",
  shortAnswer:
    "En Neuro Wellness, los pacientes de pago directo suelen pasar de la consulta al tratamiento en unos días a dos semanas, según la disponibilidad del escáner de fMRI y la programación de la sala de tratamiento. El proceso tiene tres pasos: una evaluación clínica gratuita (en persona o por telemedicina), una resonancia funcional (fMRI) programada localmente o al llegar, y la semana de tratamiento de 5 días. Los bloques de tratamiento se programan de lunes a viernes o con disponibilidad del equipo de atención durante siete días. No prometemos una fecha de inicio garantizada antes de que su evaluación y resonancia estén completas. La verificación del seguro y la autorización previa, si aplican, pueden añadir días a semanas según su aseguradora.",
  keyTakeaways: [
    "Tres pasos: consulta de evaluación, resonancia fMRI y semana de tratamiento de 5 días.",
    "Los pacientes de pago directo suelen comenzar en días a dos semanas tras su evaluación.",
    "La resonancia fMRI debe completarse y procesarse antes de comenzar el tratamiento.",
    "La verificación del seguro o la autorización previa pueden añadir días a semanas.",
    "Las consultas aclaran el calendario exacto sin prometer una fecha de inicio garantizada.",
  ],
  sections: [
    {
      heading:
        "¿Con qué rapidez puedo empezar SAINT® TMS? El calendario realista",
      paragraphs: [
        "Como SAINT® requiere una resonancia funcional personalizada antes del tratamiento, no puede empezarse el mismo día de su consulta inicial. Sin embargo, como el tratamiento en sí dura solo cinco días consecutivos (en lugar de seis semanas), el calendario general desde la consulta hasta la finalización es mucho más corto que el de la TMS convencional.",
        "Para los pacientes de pago directo, el calendario típico desde la primera llamada hasta el día uno del tratamiento es de unos días a dos semanas. La principal variable es programar la resonancia fMRI y permitir tiempo para que el software de Magnus Medical procese los datos de imagen y calcule su objetivo personalizado.",
      ],
    },
    {
      heading: "Los pasos antes del tratamiento",
      paragraphs: [
        "Antes de empezar SAINT®, tres pasos operativos deben completarse:",
      ],
      list: [
        "Evaluación clínica: una consulta gratuita con nuestro equipo clínico (en persona o por telemedicina) para confirmar la candidatura y revisar la seguridad médica/de la resonancia.",
        "Resonancia fMRI: una resonancia estructural y funcional en reposo para identificar su objetivo de estimulación personalizado. Los pacientes de fuera del estado pueden completarla al llegar o en un centro de imagen asociado antes de viajar.",
        "Programación de la semana de tratamiento: reservar una sala privada de tratamiento durante 5 días consecutivos (10 sesiones al día con descansos de 50 minutos).",
        "Verificación del seguro / autorización previa (si aplica): confirmar la cobertura y presentar la documentación de autorización previa si usa seguro.",
      ],
    },
    {
      heading: "Calendario: pago directo vs seguro",
      paragraphs: [
        "Los pacientes de pago directo se mueven más rápido porque no se requiere autorización previa del seguro. Una vez que su evaluación confirma la candidatura y su resonancia fMRI se completa, el tratamiento puede comenzar tan pronto como haya una sala disponible.",
        "Si busca cobertura del seguro o un acuerdo de caso único, la autorización previa suele tomar de 1 a 4 semanas según su plan. Verificamos los beneficios de su seguro sin costo al inicio para que sepa dónde está su plan.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
